import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Box,
  Typography,
  Divider,
  useTheme,
  Stack,
  Tooltip,
  Switch,
  FormControlLabel,
  Breadcrumbs,
  Link,
  CircularProgress,
  Skeleton,
  Chip,
  Button,
  Paper,
  Grid,
  Backdrop,
  Alert,
} from "@mui/material";
import { alpha } from "@mui/material";
import RocketLaunchRounded from "@mui/icons-material/RocketLaunchRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useParams, useNavigate } from "react-router-dom"; // <-- NOVO
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "react-query";
import PipelineStage from "../../components/SalesPipeline/PipelineStage";
import { useSalesPipelineStages } from "../../hooks/useSalesPipelineStages";
import {
  useSalesPipelineCards,
  mapSalesPipelineLeadRow,
} from "../../hooks/useSalesPipelineCards";
import OrderDetailsDialog from "../../components/SalesPipeline/OrderDetailsDialog";
import { fetchData, getConversationById, getRequestDetails } from "../../api/client";
import { SALES_PIPELINE_UNSET_SUBTIPO1_STAGE_ID } from "../../data/salesPipeline/constants";
import useChatState from "../Chat/hooks/useChatState";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";

/** Tamanho da página por coluna */
const PAGE_SIZE = 50;

/** Monta card no formato do pipeline a partir de GET /requests/:id (fallback do deep link). */
function mapRequestDetailToPipelineCard(row) {
  if (!row || row.id == null) return null;
  return mapSalesPipelineLeadRow({
    id: Number(row.id),
    status: row.status,
    orderCode: `COD-${row.id}`,
    companyName: row.cliente_nome || "Cliente",
    createdAt: row.dataproximocontato || null,
    createdAtLabel: row.dataproximocontato || null,
    channelType: row.origem || "Site",
    valor: row.valor != null ? Number(row.valor) : 0,
    items: [],
    client: row.cliente_nome ? { name: row.cliente_nome, type: "PF" } : null,
    payment: null,
    shipping: null,
    address: null,
    user: null,
  });
}

/* ===========================
   Componente filho (BOARD)
   - carrega e renderiza os cards
   - expõe refreshAll() via ref para recarregar sem limpar os cards
   =========================== */
const PipelineBoard = forwardRef(function PipelineBoard(
  {
    visibleStages,
    depth,
    pipelineTipoId,
    compact,
    onClickStageTitle,
    onInitialLoadDone, // avisa o pai quando concluiu o 1º carregamento
    softRefresh,       // função do pai para recarregar sem limpar os cards
    onSuccessfulStageMove, // após mover card entre colunas (ex.: atualizar probe "Sem definição")
  },
  ref
) {
  const containerRef = useRef(null);

  const {
    cardsByStage,
    hasMoreByStage,
    loadingByStage,
    totalsByStage,
    stageSearchDisplayByStage,
    loadMore,
    moveCardBetweenStages,
    updateLeadStageAsync,
    refreshAll, // <-- recarrega sem limpar
    applyStageSearch,
  } = useSalesPipelineCards({
    visibleStages,
    level: depth,
    limit: PAGE_SIZE,
    pipelineTipoId,
  });

  // Expor método imperativo ao pai
  useImperativeHandle(ref, () => ({
    refreshAll: () => refreshAll(),
  }));

  // Soma total de cards p/ as barras de proporção
  const totalCards = useMemo(
    () =>
      Object.values(cardsByStage).reduce(
        (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0),
        0
      ),
    [cardsByStage]
  );

  // ======= DnD =======
  const [draggedCard, setDraggedCard] = useState(null); // { card, fromStageId }
  const [draggingOverStage, setDraggingOverStage] = useState(null);
  const [draggingOverIndex, setDraggingOverIndex] = useState(null);

  const [blockAlert, setBlockAlert] = useState(null);

  const handleDragStart = (e, card, fromStageId) => {
    setDraggedCard({ card, fromStageId });
    try {
      e.dataTransfer.setData("text/plain", String(card.id));
    } catch {}
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, stageId, index = null) => {
    e.preventDefault();
    setDraggingOverStage(stageId);
    setDraggingOverIndex(index);
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e, targetStageId) => {
    e.preventDefault();
    setDraggingOverStage(null);
    setDraggingOverIndex(null);

    const moving = draggedCard;
    setDraggedCard(null);
    if (!moving || !moving.card || !moving.fromStageId || !targetStageId) return;
    if (moving.fromStageId === targetStageId) return;

    // otimista
    moveCardBetweenStages(moving.fromStageId, targetStageId, moving.card.id);

    try {
      await updateLeadStageAsync(moving.card.id, depth, targetStageId);
      onSuccessfulStageMove?.();
    } catch (err) {
      console.error("Erro ao mover card:", err);
      // rollback visual
      moveCardBetweenStages(
        targetStageId,
        moving.fromStageId,
        moving.card.id
      );

      // captura mensagem do backend
      const msg =
        err?.message ||
        err?.response?.message ||
        err?.response?.data?.message ||
        "Esta ação foi bloqueada por uma regra de automação.";

      setBlockAlert(msg);
    }
  };

  // ======= Sinaliza ao pai quando terminou o 1º carregamento do board =======
  const stageIds = useMemo(
    () => (visibleStages || []).map((s) => s.id),
    [visibleStages]
  );

  useEffect(() => {
    if (!onInitialLoadDone) return;
    if (!stageIds.length) return;

    // "Carregado" quando TODAS as colunas visíveis não estão mais com loading
    const allLoaded =
      stageIds.every((id) => loadingByStage[id] === false || loadingByStage[id] == null) &&
      stageIds.every((id) => Array.isArray(cardsByStage[id])); // arrays inicializados

    if (allLoaded) onInitialLoadDone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(stageIds), loadingByStage, cardsByStage]);

  return (
    <>
      <Box
        ref={containerRef}
        sx={{ display: "flex", gap: 2, overflowX: "auto", scrollBehavior: "smooth", pb: 2 }}
      >
        {(visibleStages || []).map((stage) => {
          const cards = cardsByStage[stage.id] || [];
          const isLoadingStage = !!loadingByStage[stage.id];
          const hasMore = !!hasMoreByStage[stage.id];

          return (
            <Box key={stage.id} sx={{ position: "relative" }}>
              {/* Spinner do estágio (apenas para os CARDS) */}
              {isLoadingStage && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    zIndex: 2,
                    pointerEvents: "none",
                  }}
                >
                  <CircularProgress size={18} />
                </Box>
              )}

              <PipelineStage
                stage={{
                  id: stage.id,
                  name: stage.nome,
                  children: stage.children || [],
                  isUnsetSubtipo1: stage.isUnsetSubtipo1,
                }}
                cards={cards}
                onStageDragOver={handleDragOver}
                onStageDrop={handleDrop}
                onCardDragStart={handleDragStart}
                // onDragOver={handleDragOver}
                // onDrop={handleDrop}
                // onDragStart={handleDragStart}
                draggingOverStage={draggingOverStage}
                draggingOverIndex={draggingOverIndex}
                draggedCard={draggedCard}
                totalCards={totalCards}
                compact={compact}
                onOpenDetails={() => {}}
                onStageClick={() => onClickStageTitle(stage)}
                onLoadMore={() => loadMore(stage.id)}
                hasMore={hasMore}
                loadingMore={isLoadingStage}
                softRefresh={softRefresh}
                stageTotalCount={totalsByStage[stage.id]}
                stageSearchQuery={stageSearchDisplayByStage[stage.id] || ""}
                onApplyStageSearch={(q) => applyStageSearch(stage.id, q)}
              />
            </Box>
          );
        })}
      </Box>
      <Dialog open={!!blockAlert} onClose={() => setBlockAlert(null)}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          ⚠️ Ação bloqueada
        </DialogTitle>
        <DialogContent>
          <Typography>{blockAlert}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockAlert(null)}>
            Entendi
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

/* ===========================
   Página principal
   =========================== */
export default function SalePipeline() {
  const theme = useTheme();
  const { t } = useTranslation("settings");
  const queryClient = useQueryClient();

  // ID opcional da rota: /sales-pipeline/:id
  const { id: routeId } = useParams();
  const navigate = useNavigate();

  const [urlLeadCard, setUrlLeadCard] = useState(null);
  const [urlLeadDialogOpen, setUrlLeadDialogOpen] = useState(false);
  const [urlLeadLoading, setUrlLeadLoading] = useState(false);
  const [urlLeadNotFound, setUrlLeadNotFound] = useState(false);

  // Compacto/detalhado
  const [compact, setCompact] = useState(true);

  // Árvore de etapas (tipos) e trilha
  const {
    loading: loadingStages,
    error: stagesError,
    visibleStages, // nós visíveis no nível atual
    path, // trilha atual (array de nós)
    goToRoot,
    drillDown,
  } = useSalesPipelineStages();

  const depth = path.length; // 0 => id_tipo, 1 => id_subtipo1, ...
  const hasStages = Array.isArray(visibleStages) && visibleStages.length > 0;

  /** Tipo comercial pai ao ver subtipos (nível 1) — escopo da coluna "sem etapa" e do probe. */
  const pipelineParentTipoId = useMemo(() => {
    if (!path.length) return null;
    const raw = path[0]?.id;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [path]);

  const { data: unsetSubtipo1Total = 0, isLoading: unsetProbeLoading } = useQuery(
    ["salesPipelineUnsetSubtipo1Probe", depth, pipelineParentTipoId],
    async () => {
      const qs = new URLSearchParams({
        id_subtipo1_unset: "1",
        page: "1",
        limit: "1",
        include_products: "1",
      });
      if (pipelineParentTipoId != null) {
        qs.set("id_tipo", String(pipelineParentTipoId));
      }
      const resp = await fetchData(`/sales-pipeline/leads?${qs.toString()}`, "GET");
      return Number(resp?.pagination?.total ?? 0);
    },
    {
      enabled: depth === 1 && hasStages,
      staleTime: 0,
      refetchOnWindowFocus: true,
    }
  );

  const visibleStagesForBoard = useMemo(() => {
    const showUnsetColumn =
      depth === 1 &&
      hasStages &&
      (unsetSubtipo1Total > 0 || unsetProbeLoading);
    if (!showUnsetColumn) return visibleStages;
    const unsetStage = {
      id: SALES_PIPELINE_UNSET_SUBTIPO1_STAGE_ID,
      nome: t("salesPipelinePage.unsetSubtype1Column"),
      children: [],
      isUnsetSubtipo1: true,
    };
    return [unsetStage, ...visibleStages];
  }, [depth, hasStages, t, unsetSubtipo1Total, unsetProbeLoading, visibleStages]);

  const notifySuccessfulStageMove = useCallback(() => {
    if (depth !== 1) return;
    void queryClient.refetchQueries(["salesPipelineUnsetSubtipo1Probe"]);
  }, [depth, queryClient]);

  // ======= Criar nova solicitação (Comercial) =======
  const { createConversation, isCreatingConversation } = useChatState();

  // Deep link /sales-pipeline/:id — relacionamento → Chat; senão pipeline → fallback GET /requests/:id
  useEffect(() => {
    if (!routeId) {
      setUrlLeadCard(null);
      setUrlLeadDialogOpen(false);
      setUrlLeadLoading(false);
      setUrlLeadNotFound(false);
      return;
    }

    const idNum = Number(routeId);
    if (!Number.isFinite(idNum)) {
      setUrlLeadLoading(false);
      return;
    }

    let cancelled = false;
    setUrlLeadLoading(true);
    setUrlLeadNotFound(false);

    (async () => {
      try {
        let numericTypeType = null;
        try {
          const conv = await getConversationById(routeId);
          if (cancelled) return;

          const d = conv || {};
          const req = d.request ?? d.solicitation ?? d.demand ?? d;
          const rawTypeType = d.typeType ?? req.typeType ?? null;
          numericTypeType =
            rawTypeType !== null && rawTypeType !== undefined
              ? Number(rawTypeType)
              : null;
        } catch {
          // Falha ao classificar pela conversa: segue tentando o pipeline comercial.
        }

        if (cancelled) return;

        if (numericTypeType === 1) {
          navigate(`/chat/${routeId}`, { replace: true });
          return;
        }

        const qs = new URLSearchParams({
          search: String(idNum),
          page: "1",
          limit: "500",
          include_products: "1",
        });
        const resp = await fetchData(`/sales-pipeline/leads?${qs.toString()}`, "GET");
        const rows = Array.isArray(resp?.data) ? resp.data : [];
        const cards = rows.map(mapSalesPipelineLeadRow);
        let hit = cards.find((c) => c.id === idNum);

        if (!hit && !cancelled) {
          try {
            const detail = await getRequestDetails(routeId);
            if (cancelled) return;
            if (detail && Number(detail.tipo_tipo) === 1) {
              navigate(`/chat/${routeId}`, { replace: true });
              return;
            }
            if (detail && detail.id != null) {
              hit = mapRequestDetailToPipelineCard(detail);
            }
          } catch {
            /* 404 ou sem acesso */
          }
        }

        if (cancelled) return;

        if (hit) {
          setUrlLeadCard(hit);
          setUrlLeadDialogOpen(true);
          setUrlLeadNotFound(false);
        } else {
          setUrlLeadNotFound(true);
        }
      } catch (err) {
        console.error("Erro ao carregar solicitação pelo ID no SalesPipeline:", err);
        if (!cancelled) setUrlLeadNotFound(true);
      } finally {
        if (!cancelled) setUrlLeadLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [routeId, navigate]);

  const handleCloseUrlLeadDialog = () => {
    setUrlLeadDialogOpen(false);
    setUrlLeadCard(null);
    setUrlLeadNotFound(false);
    if (routeId) {
      navigate("/sales-pipeline", { replace: true });
    }
  };

  // Estados de refresh
  const [isRefreshingBoard, setIsRefreshingBoard] = useState(false);
  const boardRef = useRef(null);

  // Refresh suave (não remove cards): chama o método do filho
  const softRefresh = async () => {
    if (!boardRef.current || typeof boardRef.current.refreshAll !== "function") return;
    try {
      setIsRefreshingBoard(true);
      await boardRef.current.refreshAll();
      await queryClient.invalidateQueries(["salesPipelineUnsetSubtipo1Probe"]);
    } finally {
      setIsRefreshingBoard(false);
    }
  };

  // O filho avisa quando finalizou o 1º carregamento das colunas
  const handleBoardReady = () => setIsRefreshingBoard(false);

  // Auto-refresh ao voltar o foco/visibilidade (sem limpar cards)
  useEffect(() => {
    const onFocus = () => softRefresh();
    const onVisibility = () => {
      if (document.visibilityState === "visible") softRefresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleCreateCommercialLead() {
    try {
      // mesmo padrão do Chat: inferir projectId do sessionStorage
      let projectId = null;
      try {
        const raw = sessionStorage.getItem("selectedCompanies");
        const arr = JSON.parse(raw || "[]");
        if (Array.isArray(arr) && arr.length > 0) projectId = Number(arr[0]);
      } catch {}

      const payload = {
        projectId: projectId || undefined,
        client: {},
        typeId: undefined, // backend resolve por default
        initialMessage: "",
        tipo_tipodelead: 0, // 0 = comercial (filtro no handler)
      };

      await createConversation(payload);

      // soft refresh após criar (sem limpar)
      await softRefresh();
    } catch (err) {
      console.error("Falha ao criar solicitação comercial:", err);
      alert("Não foi possível criar a solicitação comercial. Tente novamente.");
    }
  }

  const onClickStageTitle = (stage) => {
    if ((stage?.children || []).length > 0) drillDown(stage.id);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: theme.palette.background.default, minHeight: "100vh" }}>
      <Stack direction="row" alignItems="center" spacing={2} mb={0.5}>
        <Typography variant="h4" color={theme.palette.text.primary}>
          {t("salesPipelinePage.title")}
        </Typography>

        <Box sx={{ flex: 1 }} />

        <FormControlLabel
          sx={{ ml: -1 }}
          control={<Switch checked={compact} onChange={(e) => setCompact(e.target.checked)} />}
          label={compact ? t("salesPipelinePage.compact") : t("salesPipelinePage.detailed")}
        />

        {/* Botão: Atualizar (manual) — soft refresh */}
        <Tooltip title="Atualizar cards (R)">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={!isRefreshingBoard ? <RefreshRounded /> : null}
              onClick={softRefresh}
              disabled={loadingStages || isRefreshingBoard}
              sx={{ borderRadius: 2, fontWeight: 700 }}
            >
              {isRefreshingBoard ? <CircularProgress size={18} color="inherit" /> : t("salesPipelinePage.refresh")}
            </Button>
          </span>
        </Tooltip>

        {/* Botão: Nova solicitação (Comercial) — com loading do Chat */}
        <Button
          size="small"
          variant="contained"
          startIcon={!isCreatingConversation ? <RocketLaunchRounded /> : null}
          onClick={handleCreateCommercialLead}
          disabled={isCreatingConversation}
          sx={{ borderRadius: 2, fontWeight: 700 }}
        >
          {isCreatingConversation ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            t("salesPipelinePage.newRequestCommercial")
          )}
        </Button>
      </Stack>

      {/* Breadcrumbs (Raiz / ... ) */}
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link underline="hover" color="inherit" onClick={goToRoot} sx={{ cursor: "pointer" }}>
          {t("salesPipelinePage.root")}
        </Link>
        {path.map((node) => (
          <Typography key={node.id} color="text.primary">
            {node.nome}
          </Typography>
        ))}
      </Breadcrumbs>

      {urlLeadNotFound && routeId && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          onClose={() => {
            setUrlLeadNotFound(false);
            navigate("/sales-pipeline", { replace: true });
          }}
        >
          {t("salesPipelinePage.urlLeadNotFound", { id: routeId })}
        </Alert>
      )}

      {/* <Typography variant="body1" color={theme.palette.text.secondary} gutterBottom>
        Acompanhe o progresso de pedidos e carrinhos: pagamento, antifraude, separação/expedição,
        transporte e entrega — incluindo cancelamentos, devoluções e chargeback.
      </Typography> */}
      <Divider sx={{ mb: 2, backgroundColor: theme.palette.divider }} />

      {/* Skeleton do board enquanto carrega as etapas */}
      {loadingStages && (
        <Box sx={{ display: "flex", gap: 2, overflowX: "auto", pb: 2 }}>
          {[1, 2, 3, 4].map((col) => (
            <Box
              key={col}
              sx={{
                minWidth: 280,
                width: 280,
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
                p: 1.5,
                bgcolor: "background.paper",
              }}
            >
              <Skeleton variant="text" width="70%" height={32} sx={{ mb: 2 }} />
              <Skeleton variant="rounded" width="100%" height={100} sx={{ mb: 1.5 }} />
              <Skeleton variant="rounded" width="100%" height={100} sx={{ mb: 1.5 }} />
              <Skeleton variant="rounded" width="100%" height={100} />
            </Box>
          ))}
        </Box>
      )}

      {/* Erro ao carregar etapas */}
      {!loadingStages && stagesError && (
        <Box sx={{ py: 2 }}>
          <Chip color="error" label={stagesError?.message || "Erro ao carregar etapas."} />
        </Box>
      )}

      {/* Empty state quando não há tipos comerciais (subtipos) configurados */}
      {!loadingStages && !stagesError && !hasStages && (
        <Paper
          variant="outlined"
          sx={{
            width: "100%",
            maxWidth: 960,
            mx: "auto",
            mt: 2,
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.25 : 0.18)}`,
            background: theme.palette.mode === "dark"
              ? `linear-gradient(160deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 50%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`
              : `linear-gradient(160deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.06)} 40%, ${theme.palette.background.paper} 100%)`,
            boxShadow: theme.palette.mode === "dark"
              ? `0 12px 40px ${alpha(theme.palette.common.black, 0.35)}`
              : `0 12px 40px ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 56,
                    height: 56,
                    flexShrink: 0,
                    borderRadius: "50%",
                    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.2 : 0.12),
                  }}
                >
                  <ShoppingCartOutlinedIcon sx={{ fontSize: 32 }} color="primary" />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                    Nenhuma etapa do pipeline de vendas configurada
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.75 }}>
                    As etapas do pipeline de vendas são definidas nos tipos comerciais: cada subtipo de um tipo comercial vira uma coluna do funil. Para usar esta tela, cadastre primeiro os tipos e subtipos comerciais na tela de tipos.
                  </Typography>
                </Box>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" label="Tipos comerciais" variant="outlined" />
                <Chip size="small" label="Subtipos como etapas" variant="outlined" />
                <Chip size="small" label="Funil de vendas organizado" variant="outlined" />
              </Stack>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate("/settings/types")}
                sx={{
                  py: 1.5,
                  px: 3,
                  fontWeight: 600,
                  borderRadius: 2,
                  boxShadow: 2,
                  "&:hover": { boxShadow: 4 },
                }}
              >
                Ir para cadastro de tipos comerciais
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Board (cards) */}
      {!loadingStages && !stagesError && hasStages && (
        <Box className="tour-sales-pipeline-board">
        <PipelineBoard
          ref={boardRef}
          visibleStages={visibleStagesForBoard}
          depth={depth}
          pipelineTipoId={pipelineParentTipoId}
          compact={compact}
          onClickStageTitle={onClickStageTitle}
          onInitialLoadDone={handleBoardReady}
          softRefresh={softRefresh}
          onSuccessfulStageMove={notifySuccessfulStageMove}
        />
        </Box>
      )}

      <OrderDetailsDialog
        open={urlLeadDialogOpen}
        onClose={handleCloseUrlLeadDialog}
        card={urlLeadCard}
      />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (z) => z.zIndex.drawer + 10,
        }}
        open={urlLeadLoading && !!routeId}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress color="inherit" />
          <Typography color="inherit" variant="body2">
            {t("salesPipelinePage.urlLeadLoading")}
          </Typography>
        </Stack>
      </Backdrop>
    </Box>
  );
}